import React from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  Button,
} from '@mui/material';
import {
  ExpandMore,
  Security,
  DataUsage,
  People,
  ContactMail,
  Update,
  Shield,
  Assignment,
  Phone,
  DeleteForever,
  CloudDownload,
  Warning,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const sections = [
    {
      title: "Information We Collect",
      icon: <DataUsage />,
      content: [
        "Personal identification: Name, date of birth, national ID/passport number",
        "Contact details: Address, phone number, email address",
        "Driver's license information and driving history",
        "Academic records: Lesson logs, progress reports, test results",
        "Payment information: Transaction records (processed securely by third-party providers)",
        "Vehicle usage data: GPS location during lessons for safety and route tracking",
        "Photos and videos: For identification, certification, and instructional purposes",
        "Emergency contacts: For student safety",
        "Device information: IP address, device type, operating system (for app functionality)",
        "App usage data: Features used, session duration (for improving user experience)"
      ]
    },
    {
      title: "How We Use Your Information",
      icon: <Assignment />,
      content: [
        "Scheduling and conducting driving lessons",
        "Tracking academic progress and maintaining student records",
        "Processing payments and managing billing",
        "Sending lesson reminders, schedule updates, and important notifications via SMS, email, or push notifications",
        "Ensuring safety during driving instruction through GPS tracking",
        "Compliance with National Transport and Safety Authority (NTSA) and regulatory requirements",
        "Improving our curriculum, services, and mobile app functionality",
        "Communicating with parents/guardians of minor students",
        "Responding to emergencies and medical situations",
        "Marketing our services (with your consent, which you can withdraw anytime)",
        "Analyzing app performance and user behavior to enhance user experience"
      ]
    },
    {
      title: "Information Sharing and Disclosure",
      icon: <People />,
      content: [
        "WE DO NOT SELL, RENT, OR TRADE YOUR PERSONAL INFORMATION TO THIRD PARTIES FOR MARKETING PURPOSES",
        "National Transport and Safety Authority (NTSA) - as required by Kenyan law",
        "Insurance providers - only driving record information when legally required",
        "Parents/guardians - for students under 18 years old",
        "Emergency services - in case of accidents or medical emergencies",
        "Payment processors (Paystack, M-Pesa) - for secure transaction processing",
        "Cloud service providers (Firebase, AWS) - for secure data storage",
        "Legal authorities - when required by law or to protect rights and safety",
        "Authorized instructors and staff - on a need-to-know basis only"
      ]
    },
    {
      title: "Data Security Measures",
      icon: <Security />,
      content: [
        "End-to-end encryption for all data transmission",
        "SSL/TLS certificates for website and API communications",
        "Encrypted databases with access controls",
        "Multi-factor authentication for staff access",
        "Regular security audits and penetration testing",
        "Automatic logout after inactivity",
        "Secure password requirements (minimum 8 characters with complexity)",
        "Regular data backups stored in secure, geographically distributed servers",
        "Physical security for paper records in locked, access-controlled facilities",
        "Staff training on data protection and confidentiality",
        "Incident response plan for data breaches with immediate notification protocols"
      ]
    },
    {
      title: "Your Privacy Rights",
      icon: <Shield />,
      content: [
        "RIGHT TO ACCESS: Request a copy of your personal data we hold",
        "RIGHT TO RECTIFICATION: Correct inaccurate or incomplete information",
        "RIGHT TO ERASURE (Right to be Forgotten): Request deletion of your data (subject to legal retention requirements)",
        "RIGHT TO RESTRICTION: Limit how we use your data",
        "RIGHT TO DATA PORTABILITY: Receive your data in a structured, machine-readable format",
        "RIGHT TO OBJECT: Opt-out of marketing communications and certain data processing",
        "RIGHT TO WITHDRAW CONSENT: At any time, where processing is based on consent",
        "RIGHT TO LODGE A COMPLAINT: File complaints with the Office of the Data Protection Commissioner (ODPC) Kenya",
        "RIGHT TO KNOW: Understand how your data is collected, used, and shared"
      ]
    },
    {
      title: "Children's Privacy (Under 18)",
      icon: <Warning />,
      content: [
        "We require parental/guardian consent for students under 18 years",
        "Parents/guardians have full access to their child's data",
        "We collect only necessary information for driving instruction",
        "Parents can request deletion of their child's data at any time",
        "We do not knowingly market to children without parental consent",
        "Enhanced security measures for minors' personal information"
      ]
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 3, md: 5 },
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f8faff 0%, #e3f2fd 100%)'
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Box
            sx={{
              display: 'inline-flex',
              p: 2,
              borderRadius: '50%',
              bgcolor: '#90caf9',
              mb: 3
            }}
          >
            <Security sx={{ fontSize: 40, color: '#0d47a1' }} />
          </Box>
          <Typography 
            variant="h3" 
            gutterBottom 
            sx={{ 
              fontWeight: 700,
              color: 'primary.main',
              fontSize: { xs: '2rem', md: '3rem' }
            }}
          >
            Privacy Policy
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ maxWidth: 600, mx: 'auto' }}
          >
            Moxy Driving School - Your Privacy, Our Priority
          </Typography>
        </Box>

        {/* Last Updated */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 4,
            p: 2,
            bgcolor: '#fff3e0',
            borderRadius: 2,
            color: '#e65100'
          }}
        >
          <Update sx={{ mr: 1 }} />
          <Typography variant="body1" fontWeight={600}>
            Last Updated: January 2025
          </Typography>
        </Box>

        {/* Introduction */}
        <Paper sx={{ p: 3, mb: 4, bgcolor: '#e3f2fd', color: '#1565c0' }}>
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Our Commitment to Your Privacy
          </Typography>
          <Typography variant="body1" paragraph>
            At Moxy Driving School, we understand that learning to drive is a significant milestone, 
            and protecting your personal information is fundamental to our service. This Privacy Policy 
            explains how we collect, use, store, and protect your data in compliance with the Kenya Data 
            Protection Act, 2019, GDPR, and international privacy standards.
          </Typography>
          <Typography variant="body1" paragraph>
            By using our mobile application, website, or services, you agree to the collection and use 
            of information in accordance with this policy.
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            <strong>WE NEVER SELL YOUR PERSONAL INFORMATION TO THIRD PARTIES.</strong>
          </Typography>
        </Paper>

        {/* Quick Actions */}
        <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            variant="contained"
            startIcon={<DeleteForever />}
            onClick={() => navigate('/delete_data')}
            sx={{
              bgcolor: 'error.main',
              '&:hover': { bgcolor: 'error.dark' }
            }}
          >
            Request Data Deletion
          </Button>
          {/* <Button
            variant="outlined"
            startIcon={<CloudDownload />}
            onClick={() => navigate('/download-data')}
          >
            Download My Data
          </Button> */}
        </Box>

        {/* Main Sections */}
        <Box sx={{ mb: 4 }}>
          {sections.map((section, index) => (
            <Accordion 
              key={index}
              sx={{ 
                mb: 2,
                borderRadius: 2,
                '&:before': { display: 'none' },
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMore />}
                sx={{ 
                  bgcolor: 'grey.50',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ mr: 2, color: 'primary.main' }}>
                    {section.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={600}>
                    {section.title}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <List>
                  {section.content.map((item, itemIndex) => (
                    <ListItem key={itemIndex} sx={{ pl: 0 }}>
                      <ListItemIcon sx={{ minWidth: 35 }}>
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: 'primary.main'
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText 
                        primary={item}
                        sx={{ 
                          '& .MuiListItemText-primary': { 
                            fontSize: '0.95rem',
                            lineHeight: 1.6
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Additional Sections */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight={600} color="primary.main">
            Data Retention Period
          </Typography>
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="body1" paragraph>
              We retain your personal information only as long as necessary:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Student academic records: 7 years after course completion (NTSA requirement)" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Financial records: 7 years for tax compliance (Kenya Revenue Authority requirement)" 
                />
              </ListItem>
              <ListItem>
                <ListItemText primary="Communication logs: 3 years" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Marketing data: Until you unsubscribe or request deletion" />
              </ListItem>
              <ListItem>
                <ListItemText primary="App usage analytics: 2 years" />
              </ListItem>
            </List>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              After retention periods expire, we securely delete or anonymize your data.
            </Typography>
          </Paper>

          <Typography variant="h5" gutterBottom fontWeight={600} color="primary.main">
            Cookies and Tracking Technologies
          </Typography>
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="body1" paragraph>
              Our website and mobile app use cookies and similar technologies:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Essential cookies: Required for app/website functionality (login, security)" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Analytics cookies: Google Analytics, Firebase Analytics (anonymized data)" 
                />
              </ListItem>
              <ListItem>
                <ListItemText primary="Preference cookies: Remember your settings and language" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Performance cookies: Monitor app performance and crashes" />
              </ListItem>
            </List>
            <Typography variant="body2" color="text.secondary">
              You can control cookie preferences in your browser/app settings. Disabling essential 
              cookies may affect functionality.
            </Typography>
          </Paper>

          <Typography variant="h5" gutterBottom fontWeight={600} color="primary.main">
            Third-Party Services
          </Typography>
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="body1" paragraph>
              We use trusted third-party services that may collect data:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Payment Processing: Paystack, M-Pesa (PCI-DSS compliant)"
                  secondary="Privacy policies: paystack.com/privacy, safaricom.co.ke/privacy"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Cloud Storage: Google Firebase, Amazon Web Services" 
                  secondary="GDPR and ISO 27001 certified"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Analytics: Google Analytics, Firebase Analytics" 
                  secondary="Anonymized and aggregated data only"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Communication: Twilio (SMS), SendGrid (Email)" 
                  secondary="Encrypted transmission"
                />
              </ListItem>
            </List>
            <Typography variant="body2" color="text.secondary">
              These services have their own privacy policies. We ensure they meet our data protection standards.
            </Typography>
          </Paper>

          <Typography variant="h5" gutterBottom fontWeight={600} color="primary.main">
            International Data Transfers
          </Typography>
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="body1" paragraph>
              Your data may be transferred to and stored on servers outside Kenya (EU, USA) for 
              cloud hosting and backup purposes. We ensure:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Adequate protection through EU-US Data Privacy Framework compliance" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Encryption during transit and at rest" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Contractual safeguards with service providers" />
              </ListItem>
            </List>
          </Paper>

          <Typography variant="h5" gutterBottom fontWeight={600} color="primary.main">
            Data Breach Notification
          </Typography>
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="body1">
              In the unlikely event of a data breach affecting your personal information, we will:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Notify you within 72 hours via email and SMS" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Report to the Office of the Data Protection Commissioner (ODPC)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Provide details of the breach and mitigation steps" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Offer credit monitoring services if financial data was compromised" />
              </ListItem>
            </List>
          </Paper>

          <Typography variant="h5" gutterBottom fontWeight={600} color="primary.main">
            Changes to This Privacy Policy
          </Typography>
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="body1">
              We may update this Privacy Policy periodically. Changes will be:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Posted on this page with updated 'Last Updated' date" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Notified via email for significant changes" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Announced in-app with acceptance required for material changes" />
              </ListItem>
            </List>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Continued use of our services after changes constitutes acceptance of the updated policy.
            </Typography>
          </Paper>
        </Box>

        {/* Contact Information */}
        <Paper
          sx={{
            p: 4,
            bgcolor: '#bbdefb',
            color: '#0d47a1',
            borderRadius: 3,
            textAlign: 'center'
          }}
        >
          <ContactMail sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Questions or Concerns About Your Privacy?
          </Typography>
          <Typography variant="body1" paragraph>
            Contact our Data Protection Officer (DPO) for any privacy-related inquiries
          </Typography>
          
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'center',
              alignItems: 'center',
              gap: 3,
              mt: 3
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ContactMail sx={{ mr: 1 }} />
              <Typography>privacy@moxydrivingschool.co.ke</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Phone sx={{ mr: 1 }} />
              <Typography>+254 728 018 513</Typography>
            </Box>
          </Box>
          
          <Typography variant="body2" sx={{ mt: 3, opacity: 0.9 }}>
            Moxy Driving School<br />
            Ruiru Bypass, Kiambu County<br />
            Kenya
          </Typography>

          <Typography variant="body2" sx={{ mt: 2, opacity: 0.9 }}>
            Office of the Data Protection Commissioner (ODPC) Kenya<br />
            Website: odpc.go.ke | Email: complaints@odpc.go.ke
          </Typography>
        </Paper>

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'grey.200' }}>
          <Typography variant="body2" color="text.secondary" textAlign="center" paragraph>
            This Privacy Policy is governed by the laws of Kenya and complies with:
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            • Kenya Data Protection Act, 2019<br />
            • General Data Protection Regulation (GDPR)<br />
            • California Consumer Privacy Act (CCPA)<br />
            • Children's Online Privacy Protection Act (COPPA)
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy;